import React, { Component, ReactNode } from 'react';
import { logError } from '@/lib/log';

interface ErrorBoundaryProps {
    children: ReactNode;
    /** Fallback UI to render when error occurs. If not provided, uses default fallback. */
    fallback?: ReactNode;
    /** Called when error is caught */
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    /** Level of boundary: 'app' shows full-page error, 'view' shows inline error */
    level?: 'app' | 'view' | 'widget';
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/** Detects chunk/dynamic-import failures that happen after a deployment */
function isChunkLoadError(error: Error): boolean {
    const message = error.message || '';
    return (
        error.name === 'ChunkLoadError' ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Loading chunk') ||
        message.includes('Loading CSS chunk') ||
        message.includes('Importing a module script failed')
    );
}

/**
 * Dev-only: "useX must be used within XProvider" thrown from a component that
 * IS mounted under its provider. Happens when the tab misses HMR updates
 * (navigated away, websocket closed) and returns with a mixed Vite module
 * graph — two instances of the context module, so the consumer reads a
 * different context object than the mounted provider supplies. A full reload
 * rebuilds the graph consistently, so recover automatically instead of
 * stranding the developer on an error screen. Impossible in prod bundles.
 */
function isStaleHmrContextError(error: Error): boolean {
    return import.meta.env.DEV && (error.message || '').includes('must be used within');
}

/**
 * React Error Boundary component that catches rendering errors in child components.
 *
 * Usage:
 * - Wrap App root with level="app" for catastrophic failures
 * - Wrap individual views with level="view" for view-level recovery
 * - Wrap widgets with level="widget" for inline error states
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        logError(error, {
            context: 'error_boundary',
            level: this.props.level || 'view',
            componentStack: errorInfo.componentStack,
        });
        this.props.onError?.(error, errorInfo);

        // Auto-reload once for errors a reload deterministically fixes: chunk
        // load failures (stale cache after deployment) and dev-only stale-HMR
        // context errors. The sessionStorage guard prevents infinite reload loops.
        if (isChunkLoadError(error) || isStaleHmrContextError(error)) {
            const RELOAD_KEY = 'enough_chunk_reload';
            const lastReload = sessionStorage.getItem(RELOAD_KEY);
            const now = Date.now();

            if (!lastReload || now - Number(lastReload) > 10_000) {
                sessionStorage.setItem(RELOAD_KEY, String(now));
                window.location.reload();
                return;
            }
        }
    }

    handleRetry = (): void => {
        this.setState({ hasError: false, error: null });
    };

    handleReload = (): void => {
        window.location.reload();
    };

    render(): ReactNode {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            const level = this.props.level || 'view';

            // App-level error: full-page with reload option. The "new version"
            // framing is only true for chunk errors (stale deploy) — any other
            // crash gets honest copy so users aren't told a refresh brings
            // "latest features" when something actually broke.
            if (level === 'app') {
                const staleDeploy = this.state.error ? isChunkLoadError(this.state.error) : false;
                return (
                    <div className="min-h-screen bg-sand flex items-center justify-center p-6">
                        <div className="max-w-md w-full bg-cloud rounded-2xl p-8 text-center border border-mist shadow-lg">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-wildfire/10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-wildfire" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h1 className="text-2xl font-serif text-growth mb-2">
                                {staleDeploy ? 'Updates available' : 'Something went wrong'}
                            </h1>
                            <p className="text-growth/70 mb-6">
                                {staleDeploy
                                    ? 'A new version of the app is available. Please refresh the page to get the latest features.'
                                    : 'Something unexpected happened. Refreshing the page usually clears it up.'}
                            </p>
                            <button
                                onClick={this.handleReload}
                                className="w-full px-6 py-3 bg-foliage text-white rounded-xl font-medium hover:bg-foliage/90 transition-colors"
                            >
                                Refresh page
                            </button>
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <details className="mt-6 text-left">
                                    <summary className="text-sm text-growth/50 cursor-pointer hover:text-growth/70">
                                        Technical details
                                    </summary>
                                    <pre className="mt-2 p-3 bg-sand rounded-lg text-xs text-growth/70 overflow-auto max-h-40">
                                        {this.state.error.toString()}
                                        {this.state.error.stack && `\n\n${this.state.error.stack}`}
                                    </pre>
                                </details>
                            )}
                        </div>
                    </div>
                );
            }

            // View-level error: contained error with retry option
            if (level === 'view') {
                return (
                    <div className="w-full py-12 px-4">
                        <div className="max-w-md mx-auto bg-cloud rounded-2xl p-6 text-center border border-mist">
                            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-wildfire/10 flex items-center justify-center">
                                <svg className="w-6 h-6 text-wildfire" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-serif text-growth mb-2">This view couldn't load</h2>
                            <p className="text-growth/70 text-sm mb-4">
                                Something went wrong displaying this content. You can try again or navigate elsewhere.
                            </p>
                            <button
                                onClick={this.handleRetry}
                                className="px-5 py-2 bg-foliage text-white rounded-xl font-medium hover:bg-foliage/90 transition-colors"
                            >
                                Try again
                            </button>
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <details className="mt-4 text-left">
                                    <summary className="text-xs text-growth/50 cursor-pointer hover:text-growth/70">
                                        Technical details
                                    </summary>
                                    <pre className="mt-2 p-2 bg-sand rounded-lg text-xs text-growth/70 overflow-auto max-h-32">
                                        {this.state.error.toString()}
                                    </pre>
                                </details>
                            )}
                        </div>
                    </div>
                );
            }

            // Widget-level error: minimal inline error
            return (
                <div className="p-4 bg-wildfire/5 border border-wildfire/20 rounded-xl text-center">
                    <p className="text-growth/70 text-sm mb-2">Couldn't load this section</p>
                    <button
                        onClick={this.handleRetry}
                        className="text-sm text-foliage hover:text-foliage/80 font-medium transition-colors"
                    >
                        Retry
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Higher-order component to wrap a component with an error boundary.
 */
export function withErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    level: 'app' | 'view' | 'widget' = 'view'
): React.FC<P> {
    return function WithErrorBoundary(props: P) {
        return (
            <ErrorBoundary level={level}>
                <WrappedComponent {...props} />
            </ErrorBoundary>
        );
    };
}
